<script setup lang="ts">
import { useCurrentUser } from "@/api/current-user";
import { useSpace } from "@/api/space-query";
import CommonHeader from "@/components/common/CommonHeader.vue";
import Frame from "@/components/common/Frame.vue";
import { icWorkspacesOutline } from "@/components/icons";
import PermissionGuard from "@/components/permission/PermissionGuard.vue";
import CustomRouterView from "@/layouts/content/CustomRouterView.vue";
import Drawer from "@/layouts/drawer/Drawer.vue";
import DrawerLayout from "@/layouts/drawer/DrawerLayout.vue";
import { DrawerSelection } from "@/layouts/drawer/use-drawer-data";
import { useDrawerState, useDrawerStore } from "@/stores/drawer";
import { useScopeStore } from "@/stores/scope";
import { mdiEye } from "@mdi/js";
import { Button, Icon, Tooltip, useScreenSize } from "@soft-db/ui";

const { t } = useI18n();

const router = useRouter();
const route = useRoute();

const id = computed(() => route.params.id as string);
const { space } = useSpace(id);

const { currentUser } = useCurrentUser();

const { mdOrLower } = useScreenSize();
const { toggleDrawer } = useDrawerStore();

function changePage(selection: DrawerSelection) {
	router.push({
		name: "spaces-id",
		params: { id: selection.id },
		query: { ...route.query },
	});
	if (mdOrLower.value) toggleDrawer();
}

const drawerState = useDrawerState();
const scopeStore = useScopeStore();

const hasDrawer = computed(() => {
	return scopeStore.scope.type === "distributor";
});

async function viewSpace(id: string) {
	await router.push("/");
	scopeStore.setContext("space", id);
	drawerState.clear();
}
</script>

<template>
	<DrawerLayout :disabled="!hasDrawer">
		<template v-if="hasDrawer" #drawer>
			<Drawer :objects="['space']" @click="changePage" />
		</template>
		<div class="h-full overflow-auto">
			<nav
				v-if="hasDrawer"
				class="bg-base-200 z-1 sticky top-0 flex items-center gap-2 border-b p-2"
			>
				<CommonHeader :name="space?.name" :icon="icWorkspacesOutline" />
				<PermissionGuard v-if="currentUser?.scope.type === 'distributor'" permission="space.update">
					<Tooltip :tooltip="t('space.view')">
						<Button variant="soft" size="sm" color="primary" @click="() => viewSpace(id)">
							<Icon :d="mdiEye" />
						</Button>
					</Tooltip>
				</PermissionGuard>
			</nav>
			<Frame>
				<CustomRouterView />
			</Frame>
		</div>
	</DrawerLayout>
</template>
